import React from "react";
import clsx from "clsx";
import { makeStyles, fade } from "@material-ui/core/styles";

// Loader com a logo do Message Express: as duas metades do balão (laranja e
// azul) "conversam", pulando de forma alternada.
//
// Variantes:
//  - padrão: centralizado no espaço disponível (ex.: dentro de uma página)
//  - overlay: cobre o elemento pai (que precisa ter position: relative). O
//    Paper do Dialog do MUI já é relative, então basta colocá-lo dentro do
//    <Dialog>.
//  - fullScreen: cobre a tela inteira (boot do app, troca de rota).
//
// O aparecimento tem um pequeno atraso (fade-in) para não piscar em
// requisições rápidas.

const ORANGE = "#F08A3C";
const BLUE = "#2D7BE8";

const useStyles = makeStyles((theme) => ({
  "@keyframes logoLoaderLeft": {
    "0%, 60%, 100%": { transform: "translate(0, 0)" },
    "20%": { transform: "translate(-3px, -9px)" },
    "35%": { transform: "translate(0, 1px)" },
  },
  "@keyframes logoLoaderRight": {
    "0%, 15%, 75%, 100%": { transform: "translate(0, 0)" },
    "35%": { transform: "translate(3px, -9px)" },
    "50%": { transform: "translate(0, 1px)" },
  },
  "@keyframes logoLoaderShadow": {
    "0%, 60%, 100%": { transform: "scaleX(1)", opacity: 0.22 },
    "20%, 35%": { transform: "scaleX(0.8)", opacity: 0.12 },
  },
  "@keyframes logoLoaderFadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  "@keyframes logoLoaderLabel": {
    "0%, 100%": { opacity: 0.55 },
    "50%": { opacity: 1 },
  },
  "@keyframes logoLoaderPulse": {
    "0%, 100%": { opacity: 0.6 },
    "50%": { opacity: 1 },
  },
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(4),
    width: "100%",
    minHeight: 160,
    animation: "$logoLoaderFadeIn 250ms ease-out 150ms both",
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    minHeight: 0,
    padding: 0,
    backgroundColor: fade(theme.palette.background.paper, 0.88),
    backdropFilter: "blur(2px)",
  },
  fullScreen: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: theme.zIndex.modal + 1,
    minHeight: 0,
    padding: 0,
    backgroundColor: fade(theme.palette.background.default, 0.92),
    animationDelay: "0ms",
  },
  mark: {
    display: "block",
    overflow: "visible",
  },
  left: {
    transformBox: "fill-box",
    transformOrigin: "center bottom",
    animation: "$logoLoaderLeft 1.4s cubic-bezier(.45,0,.35,1) infinite",
  },
  right: {
    transformBox: "fill-box",
    transformOrigin: "center bottom",
    animation: "$logoLoaderRight 1.4s cubic-bezier(.45,0,.35,1) infinite",
  },
  shadow: {
    transformBox: "fill-box",
    transformOrigin: "center",
    fill: theme.palette.type === "dark" ? "#000" : "#0F1E36",
    animation: "$logoLoaderShadow 1.4s cubic-bezier(.45,0,.35,1) infinite",
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 0.3,
    color: theme.palette.text.secondary,
    animation: "$logoLoaderLabel 1.4s ease-in-out infinite",
  },
  "@media (prefers-reduced-motion: reduce)": {
    left: { animation: "$logoLoaderPulse 1.6s ease-in-out infinite" },
    right: { animation: "$logoLoaderPulse 1.6s ease-in-out infinite 0.4s" },
    shadow: { animation: "none" },
    label: { animation: "none" },
  },
}));

const LogoLoader = ({
  size = 56,
  label = "Carregando...",
  overlay = false,
  fullScreen = false,
  className,
}) => {
  const classes = useStyles();

  return (
    <div
      className={clsx(
        classes.root,
        overlay && classes.overlay,
        fullScreen && classes.fullScreen,
        className
      )}
      role="status"
      aria-live="polite"
      aria-label={label || "Carregando"}
    >
      <svg
        className={classes.mark}
        width={size}
        height={size * (104 / 96)}
        viewBox="0 0 96 104"
        aria-hidden="true"
      >
        <ellipse className={classes.shadow} cx="48" cy="98" rx="30" ry="3.5" />
        <path
          className={classes.left}
          d="M22,8 L46,8 L46,64 L30,64 L16,80 Q12,84 14,76 L16,64 L22,64 Q10,64 10,52 L10,20 Q10,8 22,8 Z"
          fill={ORANGE}
        />
        <path
          className={classes.right}
          d="M50,8 L74,8 Q86,8 86,20 L86,52 Q86,64 74,64 L50,64 Z"
          fill={BLUE}
        />
      </svg>
      {label ? <span className={classes.label}>{label}</span> : null}
    </div>
  );
};

export default LogoLoader;
